import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const T = {
  navy: "#002868", gold: "#C5A55A", red: "#B22234",
  text: "#1B1B1B", textMuted: "rgba(27,27,27,0.62)",
  border: "rgba(27,27,27,0.12)", cardBg: "#FFFFFF",
  shadow: "0 2px 16px rgba(0,0,0,0.07)",
  green: "#16a34a", amber: "#d97706",
};
const sans = "'Instrument Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

interface NarrationStats {
  totalScenarios: number;
  totalLines: number;
  withAudio: number;
  pendingAudio: number;
  byLanguage: { language: string; lines: number; withAudio: number }[];
  scenariosWithNarration: number;
  scenariosWithoutNarration: number;
  recentLines: {
    id: string;
    scenario_name: string;
    line_number: number;
    display_text: string;
    audio_url: string | null;
    language: string;
    created_at: string;
  }[];
  scenarioList: {
    id: string;
    name: string;
    category: string;
    line_count: number;
    audio_count: number;
    languages: string[];
  }[];
}

export function DignityNarrationDashboard() {
  const [stats, setStats] = useState<NarrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"overview" | "scenarios" | "recent">("overview");
  const [filter, setFilter] = useState<"all" | "with" | "without">("all");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Total lines by language with audio status
      const { data: langData } = await supabase.rpc("get_narration_language_stats" as any);

      // If RPC doesn't exist, fall back to direct queries
      const { data: allLines } = await supabase
        .from("dignity_narration_lines")
        .select("id, scenario_id, page_key, line_number, display_text, audio_url, language, created_at, is_active")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (!allLines) { setLoading(false); return; }

      // Compute stats from raw data
      const byLang = new Map<string, { lines: number; withAudio: number }>();
      const scenarioIds = new Set<string>();
      for (const l of allLines) {
        const lang = l.language || "en";
        const entry = byLang.get(lang) || { lines: 0, withAudio: 0 };
        entry.lines++;
        if (l.audio_url) entry.withAudio++;
        byLang.set(lang, entry);
        if (l.scenario_id) scenarioIds.add(l.scenario_id);
        if (l.page_key) scenarioIds.add(`page:${l.page_key}`);
      }

      const withAudio = allLines.filter(l => l.audio_url).length;

      // Get scenarios with narration — group by scenario
      const scenarioMap = new Map<string, { lines: any[]; }>();
      for (const l of allLines) {
        const key = l.scenario_id || `page:${l.page_key}`;
        if (!scenarioMap.has(key)) scenarioMap.set(key, { lines: [] });
        scenarioMap.get(key)!.lines.push(l);
      }

      // Get scenario names
      const scenarioUuids = allLines
        .filter(l => l.scenario_id)
        .map(l => l.scenario_id)
        .filter((v, i, a) => a.indexOf(v) === i);

      const { data: scenarioNames } = await supabase
        .from("scenarios")
        .select("id, name, category")
        .in("id", scenarioUuids.slice(0, 500));

      const nameMap = new Map<string, { name: string; category: string }>();
      for (const s of scenarioNames || []) {
        nameMap.set(s.id, { name: s.name, category: s.category || "Other" });
      }

      // Build scenario list
      const scenarioList: NarrationStats["scenarioList"] = [];
      for (const [key, val] of scenarioMap) {
        const info = nameMap.get(key) || { name: key.startsWith("page:") ? `Page: ${key.replace("page:", "")}` : "Unknown", category: "Other" };
        const langs = [...new Set(val.lines.map(l => l.language || "en"))];
        scenarioList.push({
          id: key,
          name: info.name,
          category: info.category,
          line_count: val.lines.length,
          audio_count: val.lines.filter(l => l.audio_url).length,
          languages: langs,
        });
      }
      scenarioList.sort((a, b) => a.name.localeCompare(b.name));

      // Count scenarios without narration
      const { count: totalActiveScenarios } = await supabase
        .from("scenarios")
        .select("id", { count: "exact", head: true })
        .eq("is_active", true);

      // Recent lines with scenario names
      const recentLines = allLines.slice(0, 50).map(l => ({
        id: l.id,
        scenario_name: nameMap.get(l.scenario_id || "")?.name || l.page_key || "Unknown",
        line_number: l.line_number,
        display_text: l.display_text,
        audio_url: l.audio_url,
        language: l.language || "en",
        created_at: l.created_at,
      }));

      setStats({
        totalScenarios: totalActiveScenarios || 0,
        totalLines: allLines.length,
        withAudio,
        pendingAudio: allLines.length - withAudio,
        byLanguage: [...byLang.entries()].map(([language, data]) => ({ language, ...data })).sort((a, b) => b.lines - a.lines),
        scenariosWithNarration: scenarioIds.size,
        scenariosWithoutNarration: (totalActiveScenarios || 0) - scenarioIds.size,
        recentLines,
        scenarioList,
      });
    } catch (e) {
      console.error("Failed to fetch narration stats:", e);
    }
    setLoading(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>Loading narration stats...</div>;
  if (!stats) return <div style={{ padding: 40, color: T.red }}>Failed to load stats</div>;

  const audioPct = stats.totalLines > 0 ? Math.round(100 * stats.withAudio / stats.totalLines) : 0;
  const coveragePct = stats.totalScenarios > 0 ? Math.round(100 * stats.scenariosWithNarration / stats.totalScenarios) : 0;

  const filteredScenarios = tab === "scenarios"
    ? stats.scenarioList.filter(s =>
        filter === "with" ? s.audio_count === s.line_count :
        filter === "without" ? s.audio_count < s.line_count :
        true
      )
    : [];

  return (
    <div style={{ fontFamily: sans }}>
      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Scenarios with Narration", value: stats.scenariosWithNarration, sub: `of ${stats.totalScenarios.toLocaleString()} total (${coveragePct}%)`, color: T.navy },
          { label: "Total Voice Lines", value: stats.totalLines, sub: `across ${stats.byLanguage.length} language(s)`, color: T.gold },
          { label: "With Audio", value: stats.withAudio, sub: `${audioPct}% generated`, color: T.green },
          { label: "Pending Audio", value: stats.pendingAudio, sub: "awaiting TTS generation", color: stats.pendingAudio > 0 ? T.amber : T.green },
          { label: "Scenarios Remaining", value: stats.scenariosWithoutNarration, sub: "no narration yet", color: stats.scenariosWithoutNarration > 0 ? T.red : T.green },
        ].map((card, i) => (
          <div key={i} style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow, borderTop: `3px solid ${card.color}` }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: card.color, fontFamily: serif }}>{card.value.toLocaleString()}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginTop: 4 }}>{card.label}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Language Breakdown */}
      <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow, marginBottom: 24 }}>
        <h3 style={{ fontFamily: serif, fontSize: 18, color: T.navy, marginBottom: 12 }}>By Language</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {stats.byLanguage.map(lang => (
            <div key={lang.language} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${T.border}`, minWidth: 120 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{lang.language.toUpperCase()}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>{lang.lines} lines / {lang.withAudio} audio</div>
              <div style={{ height: 4, borderRadius: 2, background: "#eee", marginTop: 6 }}>
                <div style={{ height: 4, borderRadius: 2, background: T.green, width: `${lang.lines > 0 ? Math.round(100 * lang.withAudio / lang.lines) : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {(["overview", "scenarios", "recent"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "8px 20px", borderRadius: 8, border: `1px solid ${tab === t ? T.navy : T.border}`,
            background: tab === t ? T.navy : "white", color: tab === t ? "white" : T.text,
            fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}>
            {t === "overview" ? "Audio Progress" : t === "scenarios" ? `Scenarios (${stats.scenarioList.length})` : "Recent Lines"}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "overview" && (
        <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow }}>
          <h3 style={{ fontFamily: serif, fontSize: 18, color: T.navy, marginBottom: 12 }}>Audio Generation Progress</h3>
          <div style={{ height: 24, borderRadius: 12, background: "#eee", marginBottom: 8, overflow: "hidden" }}>
            <div style={{ height: 24, borderRadius: 12, background: `linear-gradient(90deg, ${T.green}, ${T.navy})`, width: `${audioPct}%`, transition: "width 0.5s" }} />
          </div>
          <div style={{ fontSize: 13, color: T.textMuted }}>{stats.withAudio.toLocaleString()} / {stats.totalLines.toLocaleString()} lines have audio ({audioPct}%)</div>
        </div>
      )}

      {tab === "scenarios" && (
        <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {(["all", "with", "without"] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: "4px 12px", borderRadius: 6, border: `1px solid ${filter === f ? T.gold : T.border}`,
                background: filter === f ? "rgba(197,165,90,0.1)" : "white", color: T.text, fontSize: 12, cursor: "pointer",
              }}>
                {f === "all" ? "All" : f === "with" ? "Audio Complete" : "Audio Pending"}
              </button>
            ))}
          </div>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  <th style={{ textAlign: "left", padding: "8px 4px", color: T.textMuted }}>Scenario</th>
                  <th style={{ textAlign: "left", padding: "8px 4px", color: T.textMuted }}>Category</th>
                  <th style={{ textAlign: "center", padding: "8px 4px", color: T.textMuted }}>Lines</th>
                  <th style={{ textAlign: "center", padding: "8px 4px", color: T.textMuted }}>Audio</th>
                  <th style={{ textAlign: "center", padding: "8px 4px", color: T.textMuted }}>Lang</th>
                </tr>
              </thead>
              <tbody>
                {filteredScenarios.map(s => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: "6px 4px", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</td>
                    <td style={{ padding: "6px 4px", color: T.textMuted, fontSize: 11 }}>{s.category}</td>
                    <td style={{ textAlign: "center", padding: "6px 4px" }}>{s.line_count}</td>
                    <td style={{ textAlign: "center", padding: "6px 4px" }}>
                      <span style={{ color: s.audio_count === s.line_count ? T.green : T.amber, fontWeight: 600 }}>
                        {s.audio_count}/{s.line_count}
                      </span>
                    </td>
                    <td style={{ textAlign: "center", padding: "6px 4px", fontSize: 11 }}>{s.languages.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "recent" && (
        <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow }}>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {stats.recentLines.map(l => (
              <div key={l.id} style={{ padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{l.scenario_name} — Line {l.line_number}</span>
                  <span style={{ fontSize: 11, color: T.textMuted }}>
                    {l.language.toUpperCase()} · {l.audio_url ? "🔊" : "📝"} · {new Date(l.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.4 }}>{l.display_text.slice(0, 200)}{l.display_text.length > 200 ? "..." : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
